import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import {
  Calendar,
  Search,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  X,
  Play,
  StopCircle,
  Filter,
  FileText,
  ArrowLeft,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const rupiah = (n) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(Number(n) || 0);

const formatDateTime = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("id-ID", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const todayISO = () => {
  const now = new Date();
  const wib = new Date(now.getTime() + (7 * 60 - now.getTimezoneOffset()) * 60000);
  return wib.toISOString().slice(0, 10);
};

const daysAgoISO = (days) => {
  const now = new Date();
  const wib = new Date(now.getTime() + (7 * 60 - now.getTimezoneOffset()) * 60000);
  wib.setDate(wib.getDate() - days);
  return wib.toISOString().slice(0, 10);
};

export default function PetugasRekonsiliasi() {
  const navigate = useNavigate();
  const [dateFrom, setDateFrom] = useState(daysAgoISO(7));
  const [dateTo, setDateTo] = useState(todayISO());
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [totals, setTotals] = useState({ total_tagihan: 0, total_admin_fee: 0, total_bayar: 0 });
  const [showBulkCheck, setShowBulkCheck] = useState(false);
  const [recon, setRecon] = useState({});
  const [filterMode, setFilterMode] = useState("all");

  // Access guard — only logged-in petugas/kasir/agen
  useEffect(() => {
    const token = localStorage.getItem("user_token");
    if (!token) {
      toast.error("Silakan login sebagai petugas terlebih dahulu.");
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  const fetchPayments = useCallback(async () => {
    if (!dateFrom || !dateTo) {
      toast.error("Pilih rentang tanggal terlebih dahulu.");
      return;
    }
    if (dateFrom > dateTo) {
      toast.error("Tanggal mulai tidak boleh setelah tanggal akhir.");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("user_token");
      const res = await axios.get(`${API}/pdam-tebo/my-payments`, {
        params: { date_from: dateFrom, date_to: dateTo, no_pagination: true },
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = res.data?.payments || [];
      setPayments(list);
      setTotals(res.data?.totals || { total_tagihan: 0, total_admin_fee: 0, total_bayar: 0 });
      setRecon({});
      if (list.length === 0) {
        toast.info("Tidak ada setoran pada rentang tanggal ini.");
      } else {
        toast.success(`Ditemukan ${list.length} setoran.`);
      }
    } catch (err) {
      console.error("Fetch my-payments error", err);
      toast.error(err?.response?.data?.detail || "Gagal memuat data setoran.");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  const summary = useMemo(() => {
    let settled = 0;
    let unsettled = 0;
    let unchecked = 0;
    let errored = 0;
    let unsettledAmount = 0;
    for (const p of payments) {
      const key = `${p.no_sambungan}|${p.periode}`;
      const s = recon[key]?.status || "unchecked";
      if (s === "settled") settled += 1;
      else if (s === "unsettled") {
        unsettled += 1;
        unsettledAmount += Number(p.total_tagihan || 0);
      } else if (s === "error") errored += 1;
      else unchecked += 1;
    }
    return { settled, unsettled, unchecked, errored, unsettledAmount };
  }, [payments, recon]);

  const filteredPayments = useMemo(() => {
    if (filterMode === "all") return payments;
    return payments.filter((p) => {
      const key = `${p.no_sambungan}|${p.periode}`;
      const s = recon[key]?.status || "unchecked";
      if (filterMode === "settled") return s === "settled";
      if (filterMode === "unsettled") return s === "unsettled";
      if (filterMode === "unchecked") return s === "unchecked" || s === "error";
      return true;
    });
  }, [payments, recon, filterMode]);

  const openBulkCheck = () => {
    if (payments.length === 0) {
      toast.error("Belum ada data. Silakan filter tanggal dulu.");
      return;
    }
    setShowBulkCheck(true);
  };

  const applyBulkResults = (newRecon) => {
    setRecon((prev) => ({ ...prev, ...newRecon }));
  };

  const exportCSV = () => {
    if (payments.length === 0) {
      toast.error("Belum ada data untuk diekspor.");
      return;
    }
    const rows = filteredPayments.map((p) => {
      const key = `${p.no_sambungan}|${p.periode}`;
      const s = recon[key]?.status || "unchecked";
      const label =
        s === "settled" ? "Sudah Disetor" : s === "unsettled" ? "Belum Disetor" : s === "error" ? "Error Cek" : "Belum Dicek";
      return {
        no_sambungan: p.no_sambungan,
        nama: p.nama_pelanggan || "",
        alamat: p.alamat || "",
        periode: p.periode_text || p.periode,
        total_tagihan: p.total_tagihan,
        admin_fee: p.admin_fee,
        total_bayar: p.total_bayar,
        payment_date: p.payment_date,
        status_rekonsiliasi: label,
      };
    });
    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(","),
      ...rows.map((r) => headers.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `setoran_saya_${dateFrom}_sd_${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="petugas-rekonsiliasi-page">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-gradient-to-r from-cyan-600 to-sky-700 text-white shadow-md">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate("/")}
            className="p-1.5 hover:bg-white/20 rounded-lg transition"
            data-testid="rekon-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h1 className="font-bold text-base truncate">Cek Setoran Saya (Mandiri)</h1>
            <p className="text-xs opacity-90 truncate">Cocokkan pembayaran Anda dengan kantor PDAM</p>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-4 space-y-4">
        {/* Info */}
        <Card className="border-cyan-200 bg-cyan-50 dark:bg-cyan-900/20">
          <CardContent className="p-4 text-sm text-cyan-900 dark:text-cyan-100">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold mb-1">Cek Setoran Anda ke Kantor PDAM</div>
                <p className="text-xs leading-relaxed">
                  Halaman ini menampilkan <b>semua tagihan yang Anda tagih/bayarkan via aplikasi</b> pada rentang tanggal yang dipilih. Klik
                  <b> &ldquo;Cek Semua Setoran&rdquo; </b> untuk mengecek ke server PDAM satu-per-satu. Sistem memisahkan mana yang
                  <b className="text-emerald-700 dark:text-emerald-300"> sudah tercatat lunas di kantor PDAM </b> (sudah disetor)
                  dan mana yang <b className="text-rose-700 dark:text-rose-300"> masih belum lunas </b> (menunggu penyetoran).
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filter tanggal */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
              <Filter className="w-4 h-4" /> Filter Tanggal Pembayaran
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Dari Tanggal</label>
                <div className="relative">
                  <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="pl-9" data-testid="date-from-input" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Sampai Tanggal</label>
                <div className="relative">
                  <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="pl-9" data-testid="date-to-input" />
                </div>
              </div>
              <div className="flex items-end">
                <Button
                  onClick={fetchPayments}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-cyan-600 to-sky-700 hover:from-cyan-700 hover:to-sky-800"
                  data-testid="load-payments-btn"
                >
                  {loading ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Memuat...</>
                  ) : (
                    <><Search className="w-4 h-4 mr-2" /> Tampilkan</>
                  )}
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              <button type="button" onClick={() => { setDateFrom(todayISO()); setDateTo(todayISO()); }} className="px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700" data-testid="quick-today-btn">Hari ini</button>
              <button type="button" onClick={() => { setDateFrom(daysAgoISO(6)); setDateTo(todayISO()); }} className="px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700" data-testid="quick-7d-btn">7 hari terakhir</button>
              <button type="button" onClick={() => { setDateFrom(daysAgoISO(29)); setDateTo(todayISO()); }} className="px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700" data-testid="quick-30d-btn">30 hari terakhir</button>
              <button type="button" onClick={() => { const d = new Date(); const wib = new Date(d.getTime() + (7 * 60 - d.getTimezoneOffset()) * 60000); const first = new Date(wib.getFullYear(), wib.getMonth(), 1); setDateFrom(first.toISOString().slice(0, 10)); setDateTo(todayISO()); }} className="px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700" data-testid="quick-month-btn">Bulan ini</button>
            </div>
          </CardContent>
        </Card>

        {/* Ringkasan */}
        {payments.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatCard label="Total Setoran" value={payments.length.toString()} tone="slate" />
            <StatCard label="Total Tagihan" value={rupiah(totals.total_tagihan)} tone="slate" />
            <StatCard label="Sudah Disetor" value={summary.settled.toString()} tone="emerald" testId="stat-settled" />
            <StatCard label="Belum Disetor" value={summary.unsettled.toString()} hint={summary.unsettled > 0 ? rupiah(summary.unsettledAmount) : null} tone="rose" testId="stat-unsettled" />
            <StatCard label="Belum Dicek" value={(summary.unchecked + summary.errored).toString()} tone="amber" testId="stat-unchecked" />
          </div>
        )}

        {/* Action bar */}
        {payments.length > 0 && (
          <Card>
            <CardContent className="p-3 flex flex-wrap items-center gap-2">
              <Button onClick={openBulkCheck} className="bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800" data-testid="open-bulk-check-btn">
                <Play className="w-4 h-4 mr-2" /> Cek Semua Setoran
              </Button>
              <div className="flex items-center gap-1 ml-auto text-xs flex-wrap">
                <span className="text-slate-500 dark:text-slate-400 mr-1">Filter:</span>
                {[
                  { v: "all", label: "Semua" },
                  { v: "settled", label: "Sudah Disetor" },
                  { v: "unsettled", label: "Belum Disetor" },
                  { v: "unchecked", label: "Belum Dicek" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    onClick={() => setFilterMode(opt.v)}
                    className={`px-3 py-1 rounded-full border text-xs transition ${
                      filterMode === opt.v
                        ? "bg-slate-800 text-white border-slate-800 dark:bg-white dark:text-slate-900"
                        : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-50"
                    }`}
                    data-testid={`filter-${opt.v}-btn`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-csv-btn">
                <FileText className="w-4 h-4 mr-1" /> Export CSV
              </Button>
            </CardContent>
          </Card>
        )}

        {/* List pembayaran */}
        {payments.length > 0 && (
          <div className="space-y-2" data-testid="payments-list">
            {filteredPayments.length === 0 ? (
              <Card>
                <CardContent className="p-6 text-center text-sm text-slate-500">Tidak ada data sesuai filter.</CardContent>
              </Card>
            ) : (
              filteredPayments.map((p) => {
                const key = `${p.no_sambungan}|${p.periode}`;
                return <PaymentRow key={p.id || key} p={p} recon={recon[key]} testKey={key} />;
              })
            )}
          </div>
        )}

        {payments.length === 0 && !loading && (
          <Card>
            <CardContent className="p-8 text-center text-sm text-slate-500 dark:text-slate-400">
              Pilih rentang tanggal lalu klik <b>Tampilkan</b> untuk melihat setoran Anda.
            </CardContent>
          </Card>
        )}
      </div>

      {showBulkCheck && (
        <BulkReconModal payments={payments} onClose={() => setShowBulkCheck(false)} onApplyResults={applyBulkResults} initialRecon={recon} />
      )}
    </div>
  );
}

function StatCard({ label, value, hint, tone = "slate", testId }) {
  const tones = {
    slate: "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100",
    emerald: "bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-100",
    rose: "bg-rose-50 dark:bg-rose-900/30 border-rose-200 dark:border-rose-800 text-rose-800 dark:text-rose-100",
    amber: "bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-100",
  };
  return (
    <div className={`border rounded-xl p-3 ${tones[tone] || tones.slate}`} data-testid={testId || undefined}>
      <div className="text-[11px] uppercase tracking-wide font-medium opacity-70">{label}</div>
      <div className="text-lg font-bold mt-0.5 truncate">{value}</div>
      {hint && <div className="text-[11px] mt-0.5 opacity-80">{hint}</div>}
    </div>
  );
}

function PaymentRow({ p, recon, testKey }) {
  const status = recon?.status || "unchecked";
  const badge = (() => {
    if (status === "settled")
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-200 text-[11px] font-semibold">
          <CheckCircle2 className="w-3 h-3" /> Sudah Disetor
        </span>
      );
    if (status === "unsettled")
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-200 text-[11px] font-semibold">
          <AlertTriangle className="w-3 h-3" /> Belum Disetor
        </span>
      );
    if (status === "error")
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-100 text-[11px] font-semibold">
          <AlertTriangle className="w-3 h-3" /> Error
        </span>
      );
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[11px] font-semibold">
        Belum Dicek
      </span>
    );
  })();

  return (
    <Card data-testid={`payment-row-${testKey}`}>
      <CardContent className="p-3">
        <div className="flex flex-col md:flex-row md:items-center md:gap-4 gap-1">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm font-semibold text-slate-700 dark:text-slate-200">{p.no_sambungan}</span>
              <span className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{p.nama_pelanggan || "-"}</span>
              {badge}
            </div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 truncate">{p.alamat || "-"}</div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
              Periode: <b>{p.periode_text || p.periode}</b> · Dibayar: {formatDateTime(p.payment_date)}
            </div>
            {recon?.error && <div className="text-[11px] text-rose-600 mt-0.5">Error: {recon.error}</div>}
          </div>
          <div className="flex items-center gap-3 md:flex-col md:items-end">
            <div className="text-right">
              <div className="text-[11px] text-slate-500 dark:text-slate-400">Tagihan</div>
              <div className="font-mono font-semibold text-slate-800 dark:text-slate-100">{rupiah(p.total_tagihan)}</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============ BULK RECONCILIATION MODAL (petugas-scoped) ============
function BulkReconModal({ payments, onClose, onApplyResults, initialRecon }) {
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [processed, setProcessed] = useState(0);
  const [current, setCurrent] = useState(null);
  const [results, setResults] = useState({ ...(initialRecon || {}) });
  const [skipChecked, setSkipChecked] = useState(true);
  const abortRef = useRef(false);

  const groups = useMemo(() => {
    const m = new Map();
    for (const p of payments) {
      const key = `${p.no_sambungan}|${p.periode}`;
      if (skipChecked) {
        const prev = (initialRecon || {})[key]?.status;
        if (prev === "settled" || prev === "unsettled") continue;
      }
      if (!m.has(p.no_sambungan)) m.set(p.no_sambungan, []);
      m.get(p.no_sambungan).push({ periode: p.periode, key });
    }
    return Array.from(m.entries()).map(([no_samb, items]) => ({ no_samb, items }));
  }, [payments, skipChecked, initialRecon]);

  const totalGroups = groups.length;
  const pct = totalGroups > 0 ? Math.round((processed / totalGroups) * 100) : 0;

  const summary = useMemo(() => {
    let settled = 0;
    let unsettled = 0;
    let errored = 0;
    for (const v of Object.values(results)) {
      if (v.status === "settled") settled += 1;
      else if (v.status === "unsettled") unsettled += 1;
      else if (v.status === "error") errored += 1;
    }
    return { settled, unsettled, errored };
  }, [results]);

  const checkOne = useCallback(async (no_samb, attempt = 1) => {
    try {
      const token = localStorage.getItem("user_token");
      const res = await axios.post(
        `${API}/pdam-tebo/check`,
        { no_sambungan: no_samb, nama: "" },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 }
      );
      const localPaid = res.data?.local_paid_bills || [];
      const unsettledSet = new Set(localPaid.map((b) => b.periode));
      return { ok: true, unsettledSet };
    } catch (err) {
      const status = err?.response?.status;
      const transient = !err?.response || status >= 500 || err.code === "ECONNABORTED";
      if (attempt < 3 && transient) {
        setCurrent((c) => (c ? { ...c, attempt: attempt + 1 } : c));
        await new Promise((r) => setTimeout(r, 1500));
        return checkOne(no_samb, attempt + 1);
      }
      return { ok: false, error: err?.response?.data?.detail || err?.message || "Gagal cek server PDAM." };
    }
  }, []);

  const start = useCallback(async () => {
    if (running || groups.length === 0) return;
    setRunning(true);
    setDone(false);
    setProcessed(0);
    abortRef.current = false;
    const acc = { ...results };
    let count = 0;
    for (let i = 0; i < groups.length; i++) {
      if (abortRef.current) break;
      const g = groups[i];
      setCurrent({ index: i, no_samb: g.no_samb, attempt: 1 });
      const r = await checkOne(g.no_samb);
      const now = new Date().toISOString();
      if (r.ok) {
        for (const item of g.items) {
          const isUnsettled = r.unsettledSet.has(item.periode);
          acc[item.key] = { status: isUnsettled ? "unsettled" : "settled", checked_at: now };
        }
      } else {
        for (const item of g.items) {
          acc[item.key] = { status: "error", error: r.error, checked_at: now };
        }
      }
      setResults({ ...acc });
      count += 1;
      setProcessed(count);
    }
    setRunning(false);
    setDone(true);
    setCurrent(null);
    onApplyResults(acc);
    const s = Object.values(acc).reduce(
      (a, v) => {
        if (v.status === "settled") a.settled += 1;
        else if (v.status === "unsettled") a.unsettled += 1;
        else if (v.status === "error") a.err += 1;
        return a;
      },
      { settled: 0, unsettled: 0, err: 0 }
    );
    if (abortRef.current) toast.info("Cek dihentikan.");
    else toast.success(`Selesai: ${s.settled} sudah disetor, ${s.unsettled} belum disetor, ${s.err} error.`);
  }, [running, groups, results, checkOne, onApplyResults]);

  const stop = () => {
    abortRef.current = true;
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-3" data-testid="bulk-recon-modal">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white p-4 flex items-center justify-between">
          <div className="min-w-0">
            <h2 className="font-bold text-base flex items-center gap-2">
              <Search className="w-4 h-4" /> Cek Setoran ke Kantor PDAM
            </h2>
            <p className="text-xs opacity-90 truncate">{totalGroups} nomor sambungan · {payments.length} tagihan · timeout 20 dtk/ID</p>
          </div>
          <button onClick={onClose} disabled={running} className="p-1 hover:bg-white/20 rounded disabled:opacity-50" data-testid="close-bulk-recon-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto">
          {!running && !done && (
            <>
              <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3 text-xs text-emerald-900 dark:text-emerald-100 space-y-1.5">
                <div className="font-semibold flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4" /> Cara Kerja
                </div>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>Setiap nomor sambungan dicek ke server PDAM (satu-per-satu).</li>
                  <li>Timeout <b>20 detik/ID</b>, retry otomatis 3× bila gagal.</li>
                  <li>Jika periode masih tercatat <b>belum lunas</b> di PDAM → status <b className="text-rose-700 dark:text-rose-300">Belum Disetor</b>.</li>
                  <li>Jika periode <b>tidak muncul</b> di daftar tagihan PDAM (sudah lunas) → status <b className="text-emerald-700 dark:text-emerald-300">Sudah Disetor</b>.</li>
                  <li>Estimasi: {Math.ceil((totalGroups * 3) / 60)} - {Math.ceil((totalGroups * 15) / 60)} menit untuk {totalGroups} ID.</li>
                </ul>
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-200 cursor-pointer" data-testid="skip-checked-label">
                <input type="checkbox" checked={skipChecked} onChange={(e) => setSkipChecked(e.target.checked)} className="w-4 h-4" data-testid="skip-checked-checkbox" />
                Lewati tagihan yang sudah dicek sebelumnya
              </label>
            </>
          )}

          {(running || done) && (
            <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-600 dark:text-slate-300">Progres</span>
                <span className="font-mono font-bold text-slate-800 dark:text-slate-100" data-testid="bulk-recon-progress">{processed} / {totalGroups} ({pct}%)</span>
              </div>
              <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-600 transition-all" style={{ width: `${pct}%` }} />
              </div>
              {current && running && (
                <div className="text-[11px] text-slate-600 dark:text-slate-300">
                  Mengecek: <span className="font-mono">{current.no_samb}</span>
                  {current.attempt > 1 && <span className="ml-2 text-amber-600">· retry #{current.attempt}</span>}
                </div>
              )}
              <div className="grid grid-cols-3 gap-2 text-[11px] pt-1">
                <div className="text-emerald-700 dark:text-emerald-300 font-semibold" data-testid="bulk-recon-settled-count">✓ Sudah: {summary.settled}</div>
                <div className="text-rose-700 dark:text-rose-300 font-semibold" data-testid="bulk-recon-unsettled-count">✗ Belum: {summary.unsettled}</div>
                <div className="text-amber-700 dark:text-amber-300 font-semibold" data-testid="bulk-recon-error-count">! Error: {summary.errored}</div>
              </div>
            </div>
          )}
        </div>

        <div className="p-3 border-t border-slate-200 dark:border-slate-700 flex gap-2">
          {!running && !done && (
            <>
              <Button variant="outline" className="flex-1" onClick={onClose} data-testid="cancel-bulk-recon-btn">Batal</Button>
              <Button onClick={start} disabled={totalGroups === 0} className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800" data-testid="start-bulk-recon-btn">
                <Play className="w-4 h-4 mr-2" /> Mulai Cek ({totalGroups})
              </Button>
            </>
          )}
          {running && (
            <Button variant="destructive" className="flex-1" onClick={stop} data-testid="stop-bulk-recon-btn">
              <StopCircle className="w-4 h-4 mr-2" /> Hentikan
            </Button>
          )}
          {done && !running && (
            <Button className="flex-1" onClick={onClose} data-testid="close-bulk-recon-done-btn">Selesai</Button>
          )}
        </div>
      </div>
    </div>
  );
}
